/*
 * Proprietary and Confidential
 *
 * Copyright (c) [2018] -  [] Marcelo H. Krebber - Munich, London 2018
 * All Rights Reserved.
 *
 * Dissemination or reproduction of this file [selectors.js] or parts within
 * via any medium is strictly forbidden unless prior written permission is obtained
 * from <marcelo.krebber@gmail.com>
 *
 * Last modified: 16.01.20, 11:26
 */

import {environment as env, getUserHome} from "../actions/environment";
import {loginStatus, logoutRequest} from "../actions";
import {useSelector} from "react-redux";
import {useLocation, useParams} from "react-router-dom";
import {
    BILLABLE,
    FREE_MEMBERSHIP,
    FREE_OF_CHARGE,
    ONLY_ME_ACCESS,
    PRIVATE_ACCESS,
    PUBLIC_ACCESS,
    RESTRICTED_ACCESS,
    STATE_BLOCKED,
    STATE_CANCELLED
} from "../actions/spaces";
import {useLanguage} from "../components/configuration/configuration";

/*
 * username - username in the url, generic or home space
 * spaceUsername - the name of a space owner
 * principalUsername - the name of the logged-in user
 */

// configuration public.homepage is configured thru server and defines the actual default public user
export const useSiteConfiguration = () => {
    const location = useLocation();
    return useSelector(state => getSiteConfiguration(state, location));
}

// configuration public.homepage is configured thru server properties and defines the actual default public user
const getSiteConfiguration = (state, location) => {
    const authorized = isAuthorized(state);
    const publicConfig = state.configuration ? state.configuration.public : null;
    const siteUsername = publicConfig ? publicConfig.homepage : env.DEFAULT_PUBLIC_USER;
    const homePage =  authorized ? `/${siteUsername}/home` : `/${env.DEFAULT_PUBLIC_USER}/home`;

    return {
        isSitePublicPage: homePage === location.pathname,
        configuration: state.configuration,
        localConfiguration: state.localconfig,
        siteUsername: siteUsername,
        publicConfig: publicConfig,
        homePage: homePage,
        calendarPage: authorized ? `/${siteUsername}/calendar` : `/${env.DEFAULT_PUBLIC_USER}/calendar`,
        eventsCalendarPage: authorized ? `/${siteUsername}/calendar?type=events` : `/${env.DEFAULT_PUBLIC_USER}/calendar?type=events`,
        festivalsCalendarPage: authorized ? `/${siteUsername}/calendar?type=festivals` : `/${env.DEFAULT_PUBLIC_USER}/calendar?type=festivals`,
        spacesCalendarPage: authorized ? `/${siteUsername}/calendar?type=spaces` : `/${env.DEFAULT_PUBLIC_USER}/calendar?type=spaces`,
    }
}

export const useLocationCategory = () => {
    const Lang = useLanguage();
    const LOCATION_CATEGORY = {
            ALL: Lang.location.category.ALL,
            DANCE_SCHOOL: Lang.location.category.DANCE_SCHOOL,
            CLUB: Lang.location.category.CLUB,
            DATING: Lang.location.category.DATING,
            ORGANIZATION: Lang.location.category.ORGANIZATION,
            OPEN_AIR: Lang.location.category.OPEN_AIR,
            CULTURAL_CENTER: Lang.location.category.CULTURAL_CENTER,
            SHOPPING_CENTER: Lang.location.category.SHOPPING_CENTER,
            // RED_LIGHT_DISTRICT: Lang.location.category.RED_LIGHT_DISTRICT,
            TRADE_FAIR: Lang.location.category.TRADE_FAIR,
            SHOP: Lang.location.category.SHOP,
            OUTLET: Lang.location.category.OUTLET,
            RESTAURANT: Lang.location.category.RESTAURANT,
            TOUR_OPERATOR: Lang.location.category.TOUR_OPERATOR,
            TRAVEL_AGENCY: Lang.location.category.TRAVEL_AGENCY,
            MUSEUM: Lang.location.category.MUSEUM,
            THEATER: Lang.location.category.THEATER,
            SALSA: Lang.location.category.SALSA,
            DEFAULT: Lang.location.category.DEFAULT,
            EVENT_OPERATOR: 'Event Operator',
            DANCE_HALL: 'Dance Hall', // Discoteca/ Salon de baile, Tanzsaal/Disco
            COFFEE_SHOP: 'Coffee Shop', // Cafeteria, Café(Coffeeshop)
    }
    return {
        categoryName: (key) => LOCATION_CATEGORY[key] ,
        LOCATION_CATEGORY: LOCATION_CATEGORY
    }
}

export const useLocationCities = () => {
    const Lang = useLanguage();
    const {homePage} = useSiteConfiguration();
    const LOCATION_CITIES = {
        MUNICH: {name: Lang.location.cities.munich, link: homePage},
        BERLIN: {name: Lang.location.cities.berlin, link: homePage},
        HAMBURG: {name: Lang.location.cities.hamburg, link: homePage},
        FRANKFURT: {name: Lang.location.cities.frankfurt, link: homePage},
        DUESSELDORF: {name: Lang.location.cities.dusseldorf, link: homePage},
        ZUERICH: {name: Lang.location.cities.zurich, link: homePage},
        WIEN: {name: Lang.location.cities.wien, link: homePage},
        MADRID: {name: Lang.location.cities.madrid, link: homePage},
        BARCELONA: {name: Lang.location.cities.barcelona, link: homePage},
    }
    const NAVIGATION_CITIES = {
        REGISTER: {name: Lang.nav.submenu.createAccount, link: '/create/account'},
        LOGIN: {name: Lang.nav.submenu.login, link: '/login'},
    }
    return {
        locationCity: (key) => LOCATION_CITIES[key],
        navigationCity: (key) => NAVIGATION_CITIES[key],
        LOCATION_CITIES: LOCATION_CITIES,
        NAVIGATION_CITIES: NAVIGATION_CITIES,
    }
}

// kind of a more simplistic variation of useSiteConfiguration(), which appears too heavy
// very early in the app bootstrap the configuration may be null
// isAllowPublicAvatar intended for main site page only
export const usePublicConfiguration =  () => {
    const location = useLocation()
    const [conf, authorized] = useSelector(state => {
        return [state.configuration, isAuthorized(state)]
    });

    // const isPublicHome = !authorized && location.pathname === getPublicUserHome();
    const isPublicHome = conf !== null && !authorized && location.pathname === getUserHome(conf.publicpage);

    return {isPublicHome: isPublicHome,
        isAllowAccountRegistration: conf == null ||  conf.accountRegistration,
        isPublicMode: conf == null || conf.publicMode,
        isAllowVideoUpload: conf == null || conf.videoUpload,
        isVideoUploadAdminOnly: conf == null || conf.videoUploadAdminOnly,
        isAllowPublicAvatar: conf == null || (!isPublicHome || conf.public.siteavatar),
        isAllowPublicComments: conf == null || conf.public.comments,
        isAllowPublicLikes: conf == null || conf.public.likes,
        publicConf: conf == null || conf.public,
    }
}

// actually the homepage of the superuser
export const useHomePage = () => {
    return useSelector(state => resolveHomePage(state));
}

// configuration public.homepage is configured on the server and defines the actual default public user
// @Deprecated use useSiteConfiguration instead
export const resolveHomePage = state => {
    const {configuration} = state;
    const siteUsername = configuration && configuration.public.homepage ? configuration.public.homepage : null;
    return isAuthorized(state) && siteUsername
        ? `/${siteUsername}/home`
        : `/${env.DEFAULT_PUBLIC_USER}/home`;
};

export const isTransitioning = state => {
    const {authorization} = state;
    return authorization.status === loginStatus.REQUEST || authorization.status === loginStatus.LOGOUT ||
        authorization.status === loginStatus.ERROR;
};

export const logoutUser = dispatch => {
    dispatch(logoutRequest());
};

export const isAuthorized = state => state.authorization && state.authorization.status === loginStatus.SUCCESS;
export const isSuperUser = state => isAuthorized(state) && state.authorization.principal.isSuperUser;
export const isAdmin = state => isAuthorized(state) && state.authorization.principal.isAdmin;
export const isRegistration = state => !isAuthorized(state) && state.configuration && state.configuration.public.registration;

// basically the authorized principal - principal = authorized user
export const useAuthorization = () => {
    return useSelector(state => getAuthorization(state));
}
export const useSearchResult = () => {
    return useSelector(state => state.search)
}

export const useIsPublicGenericSpace = () => {
    return useSelector(state => getIsPublicGenericSpace(state.genericdata))
}
// refactor to use a hook and go thru the state instead
export const getIsPublicGenericSpace = (genericdata) => genericdata && genericdata.space
    ? genericdata.space.access === PUBLIC_ACCESS
    : false;

export const useNotifications = () => {
    return useSelector( state => state.notifications);
}

// detailed ie enriched info about the authorized principal
export const usePrincipalLoginData = () => {
    return useSelector(state => getPrincipalLoginData(state))
}
export const usePrincipalUsername = () => {
    return useSelector(state => getAuthorizedUsername(state));
}

// currently loaded generic space
export const useGenericData = () => {
    return useSelector(state => getSpaceGenericData(state));
}

export const usePostComments = (postId) => {
    return useSelector(state => state.comments ? state.comments[postId] : null);
}

// this is the data about the principal for a home page
export const useHomeData = () => {
    const homedata = useSelector(state => getHomeData(state));
    return homedata ? {
        homedata: homedata,
        space: homedata.space,
        user: homedata.space ? homedata.space.user : null,
        residence: homedata.userdata ? homedata.userdata.address.city : null,
        userdata: homedata.userdata,
        isOwner: homedata.isOwner,
        isFriend: homedata.isFriend,        // homespace user ie. owner is friend from principal
        calendarVisibility: getCalendarVisibility(homedata),
        friendsVisibility: getFriendsVisibility(homedata),
    } : {
        homedata: null,
        space: null,
        userdata: null,
        residence: null,
        user: null,
        isOwner: false,
        isFriend: false,
        calendarVisibility: getCalendarVisibility(null),
        friendsVisibility: getFriendsVisibility(null),
    }
}

const getCalendarVisibility = (homedata) => {
    return homedata && homedata.userdata ? {
        isPublic: homedata.userdata.calendarVisibility === 'PUBLIC',
        isFriends: homedata.userdata.calendarVisibility === 'FRIENDS',
        isNone: homedata.userdata.calendarVisibility === 'NONE',
    } : {isPublic: false, isFriends: false, isNone: true}
}

const getFriendsVisibility = (homedata) => {
    return homedata && homedata.userdata ? {
        isPublic: homedata.userdata.friendsVisibility === 'PUBLIC',
        isFriends: homedata.userdata.friendsVisibility === 'FRIENDS',
        isNone: homedata.userdata.friendsVisibility === 'NONE',
    } : {isPublic: false, isFriends: false, isNone: true}
}

export const usePublicSpaces = () => {
    return useSelector(state => getPublicSpaces(state));
}
export const useViewSpaces = () => {
    return useSelector(state => getViewSpaces(state));
}
// list of loaded generic spaces ie data in public(anonymous) and logged-in modes
export const useGenericSpaces = () => {
    return useSelector(state => getGenericSpaces(state));
}

export const useGalleryChanges = () => {
    return useSelector(state => {
        return {
            action: state.galleries ? state.galleries.gallery.action : null,
            spaceId: state.galleries ? state.galleries.gallery.spaceId : null,
            space: state.galleries ? state.galleries.gallery.space : null,
        }
    });
}

export const useUploadChanges = () => {
    return useSelector(state => {
        return {
            action: state.notifications ? state.notifications.upload.action : null,
            status: state.notifications ? state.notifications.upload.status : null,
            payload: state.notifications ? state.notifications.upload.payload : null,
        }
    });
}

// a simple hook for determining if a current location is a home space
export const useIsHomeSpaceLocation = () => {
    const {username} = useParams();
    const location = useLocation();

    return {
        isHomeSpace: username ? location.pathname === `/${username}/home` : false,
        pathname: location.pathname
    };
}

export const useToggleComments = () => {
    return useSelector(state => {
        return {
            action: state.notifications ? state.notifications.comments.action : null,
            toggle: state.notifications ? state.notifications.comments.toggle : null,
            postId: state.notifications ? state.notifications.comments.postId : null,
        }
    });
}
export const useFriendData = () => {
    return useSelector(state => getFriendData(state));
}
export const getSpaceGenericData = (state) => {
    const genericdata = getGenericData(state);
    return genericdata ? {
        // const isOwner = genericdata.isMember && genericdata.member.role === 'OWNER';
        isOwner: getAuthorizedUsername(state) === genericdata.space.user.username,
        isMembersOnly: genericdata.space.access === RESTRICTED_ACCESS,
        isRestricted: genericdata.space.access === RESTRICTED_ACCESS,
        isPublic: genericdata.space.access === PUBLIC_ACCESS,
        isPrivate: genericdata.space.access === PRIVATE_ACCESS,
        isOnlyMe: genericdata.space.access === ONLY_ME_ACCESS,
        isMember: genericdata.isMember,
        isBlocked: genericdata.space.state === STATE_BLOCKED,
        isCancelled: genericdata.space.state === STATE_CANCELLED,
        isEvent: genericdata.space.type === 'EVENT',
        isBillable: genericdata.space.fees === BILLABLE,
        isFree: genericdata.space.fees === FREE_OF_CHARGE,
        isLegitimate: genericdata.space.hygiene === 'LEGITIMATE',
        isAcademic: genericdata.space.type === 'GENERIC' && genericdata.space.subType === 'ACADEMIC',
        isGroup: genericdata.space.type === 'GENERIC' && genericdata.space.subType === 'GROUP',
        isMenu: genericdata.space.type === 'GENERIC' && genericdata.space.subType === 'MENU',
        spaceUsername: genericdata.space.user.username,
        space: genericdata.space,
        genericdata: genericdata,
    } : {genericdata: null, space: null}
}

export const getAuthorization = state => {
    const authorization = state.authorization;
    return authorization ? {
        authorization: authorization,
        isAuthorized: isAuthorized(state),
        isSuperUser: isSuperUser(state),
        isAdmin: isAdmin(state),
        superuser: env.DEFAULT_PUBLIC_USER,
        principal: authorization.principal,
        membership: authorization.principal ? authorization.principal.membership : FREE_MEMBERSHIP,
        authorizedUsername: getAuthorizedUsername(state),      // @deprecated
        principalUsername: getAuthorizedUsername(state),
        isTransitioning: isTransitioning(state),
    } : {
        authorization: null,
        isAuthorized: false,
        isSuperUser: false,
        principal: null,
        superuser: env.DEFAULT_PUBLIC_USER,
        membership: FREE_MEMBERSHIP,
        authorizedUsername: null,
        principalUsername: null,
        isTransitioning: false,
    }
};

// this is more detailed information about the authorized ie logged-in user
export const getPrincipalLoginData = state => {
    if (isAuthorized(state) && state.logindata && state.logindata.payload) {
        const logindata = state.logindata.payload;
        return {
            logindata: logindata,
            user: logindata.user,
            userdata: logindata.userdata,
            email: logindata.email,
        }
    } else return {logindata: null, user: null, userdata: null, email: null}
}

export const getPublicSpaces = state => {
    return {
        publicSpaces: state.publicSpaces,
        publicEvents: state.publicEvents,
    }
}
export const getViewSpaces = state => {
    return {
        viewSpaces: state.viewSpaces,
        viewEvents: state.viewEvents,
    }
}

// this data is returned for both logged-in and anonymous ie public user mode
export const getGenericSpaces = state => {
    return {
        spaces: state.spaces,
        events: state.events,
        widgets: state.widgets,
        media: state.media,
        members: state.members,
        member: state.member,               // current open member on panel
    }
}
export const getFriendData = state => {
    return {
        friends: state.friends,                 // list of all friends for logged in user
        pending: state.pending,                 // list of pending requests for friendship
        friend: state.friend,                   // current open friend on chat
        chatEntries: state.chatEntries,         // list of chat entries for current open chat
        chatCount: state.chatCount,             // count of delivered chat entries
        incomingChat: state.incomingChat,       // incoming chat - last delivered chat entry (may be null)
    }
}
export const getLocalConfiguration = state => {
    return state.localconfig;
}
export const getLoginData = state => {
    return state.logindata ? state.logindata.payload : null;
}
export const getLoggedInUser = state => state.logindata && state.logindata.payload ? state.logindata.payload.user : null;
export const getLoggedInUserdata = state => state.logindata && state.logindata.payload ? state.logindata.payload.userdata : null;

// @deprecated
export const isOwner = state => {
    const genericdata = getGenericData(state);
    return genericdata ? getAuthorizedUsername(state) === genericdata.space.user.username : false;
};
export const getGenericData = state => state.genericdata ? state.genericdata.payload : null;
export const getAuthorizedUsername = state => state.authorization.principal ? state.authorization.principal.username : null;
export const getLastError = state => state.error && !state.ack ? state.error : null;
export const getLastErrorFor = (state, errorCode) => state.error && state.error.dirty && state.error.data.status === errorCode ? state.error.data : null;
export const getSearchParams = (location) => location ? new URLSearchParams(location.search) : null;
export const allowComments = (state) => isAuthorized(state) || (state.configuration && state.configuration.public.comments === true);
export const allowLikes = (state) => state.configuration && state.configuration.public.likes === true;
export const getHomeData = state => state.homedata ? state.homedata.payload : null;
